import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import api from '../api/axios';
import { useAuth } from '../contexts/AuthContext';

// ─── Mini stat pill inside a module card ─────────────────────────────────────
const Pill = ({ label, value, color = 'blue' }) => {
  const colors = {
    blue:   'bg-blue-50 text-blue-700',
    green:  'bg-green-50 text-green-700',
    yellow: 'bg-yellow-50 text-yellow-700',
    red:    'bg-red-50 text-red-700',
    gray:   'bg-gray-100 text-gray-500',
    orange: 'bg-orange-50 text-orange-700',
    purple: 'bg-purple-50 text-purple-700',
    teal:   'bg-teal-50 text-teal-700',
    indigo: 'bg-indigo-50 text-indigo-700',
  };
  return (
    <div className={`flex flex-col items-center px-3 py-2 rounded-lg ${colors[color]}`}>
      <span className="text-lg font-bold leading-none">{value}</span>
      <span className="text-xs mt-0.5 font-medium opacity-80">{label}</span>
    </div>
  );
};

// ─── Module card with icon, title, link, and stat pills ──────────────────────
const ModuleCard = ({ icon, title, link, pills, accent = '#2563eb' }) => (
  <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5 hover:shadow-md transition-shadow">
    <div className="flex items-center justify-between mb-4">
      <div className="flex items-center gap-2">
        <span className="text-2xl">{icon}</span>
        <h3 className="font-semibold text-gray-800 text-sm">{title}</h3>
      </div>
      <Link to={link} className="text-xs font-medium hover:underline" style={{ color: accent }}>
        View →
      </Link>
    </div>
    <div className="flex gap-2 flex-wrap">
      {pills.map((p, i) => <Pill key={i} label={p.label} value={p.value} color={p.color} />)}
    </div>
  </div>
);

// ─── Top summary quick-stat card ─────────────────────────────────────────────
const QuickStat = ({ icon, label, value, color, link }) => (
  <Link to={link || '#'} className="block bg-white rounded-xl shadow-sm border border-gray-100 p-4 hover:shadow-md transition-shadow">
    <div className="flex items-center gap-3">
      <div className="text-3xl">{icon}</div>
      <div>
        <p className={`text-2xl font-bold ${color}`}>{value}</p>
        <p className="text-xs text-gray-500 font-medium">{label}</p>
      </div>
    </div>
  </Link>
);

// ─── Currency formatter ───────────────────────────────────────────────────────
const pkr = (n) => {
  const num = Number(n) || 0;
  if (num >= 1000000) return (num / 1000000).toFixed(1) + 'M';
  if (num >= 1000) return (num / 1000).toFixed(0) + 'K';
  return num.toLocaleString();
};

export default function Dashboard() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [recentVisitors, setRecentVisitors] = useState([]);
  const [empVehicleCount, setEmpVehicleCount] = useState(null);
  const [loading, setLoading] = useState(true);

  // Read accent color from user branding
  const accent = user?.color_primary || '#1d4ed8';

  useEffect(() => {
    if (!user) return;
    const load = async () => {
      setLoading(true);
      const today = new Date().toISOString().split('T')[0];

      try { const r = await api.get('/api/dashboard'); setData(r.data); }
      catch (err) { console.error('Dashboard stats failed:', err); setData(null); }

      try { const r = await api.get('/api/visitors?date=' + today); setRecentVisitors(Array.isArray(r.data) ? r.data.slice(0, 8) : []); }
      catch { setRecentVisitors([]); }

      try { const r = await api.get('/api/employee-vehicles'); setEmpVehicleCount(Array.isArray(r.data) ? r.data.length : (r.data?.total ?? 0)); }
      catch { setEmpVehicleCount(0); }

      setLoading(false);
    };
    load();
  }, [user?.role]);

  if (loading) return (
    <div className="flex items-center justify-center h-64">
      <div className="text-center">
        <div className="text-5xl animate-pulse">🏢</div>
        <p className="text-gray-500 mt-3">Loading dashboard...</p>
      </div>
    </div>
  );

  const d = data || {};
  const visitors    = d.visitors    || {};
  const meetings    = d.meetings    || {};
  const expenses    = d.expenses    || {};
  const trips       = d.trips       || {};
  const drivers     = d.drivers     || {};
  const vehicles    = d.vehicles    || {};
  const guestRooms  = d.guest_rooms || {};
  const hotelBkgs   = d.hotel_bookings || {};
  const byCat       = expenses.by_category || {};

  const today = new Date().toLocaleDateString('en-PK', {
    weekday: 'long', year: 'numeric', month: 'long', day: 'numeric'
  });

  return (
    <div className="space-y-6">

      {/* ── Header ─────────────────────────────────────────────────────────── */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">Dashboard</h1>
          <p className="text-gray-400 text-sm mt-0.5">{today}</p>
        </div>
        {user?.company_name && (
          <span className="text-sm font-medium text-gray-500 bg-gray-100 px-3 py-1 rounded-full">
            {user.company_name}
          </span>
        )}
      </div>

      {/* ── Quick Stats Row ─────────────────────────────────────────────────── */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <QuickStat icon="👥" label="Visitors Inside Now"    value={visitors.inside ?? 0}            color="text-blue-600"   link="/visitors" />
        <QuickStat icon="🚗" label="Vehicles Available"     value={vehicles.available ?? 0}          color="text-green-600"  link="/vehicles" />
        <QuickStat icon="🧑‍✈️" label="Drivers Available"    value={drivers.available ?? 0}           color="text-teal-600"   link="/drivers" />
        <QuickStat icon="💰" label="Expenses Today (PKR)"   value={pkr(expenses.today_total)}        color="text-red-600"    link="/expenses" />
      </div>

      {/* ── Module Cards Grid ───────────────────────────────────────────────── */}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">

        {/* Visitors */}
        <ModuleCard icon="👥" title="Visitors" link="/visitors" accent={accent} pills={[
          { label: 'Inside',       value: visitors.inside ?? 0,            color: 'green'  },
          { label: 'Today Total',  value: visitors.total_today ?? 0,       color: 'blue'   },
          { label: 'Checked Out',  value: visitors.checked_out_today ?? 0, color: 'gray'   },
        ]} />

        {/* Meetings */}
        <ModuleCard icon="📅" title="Meetings" link="/meetings" accent={accent} pills={[
          { label: 'Upcoming',   value: meetings.upcoming ?? 0,        color: 'blue'   },
          { label: 'Ongoing',    value: meetings.ongoing ?? 0,         color: 'orange' },
          { label: 'Completed',  value: meetings.completed_today ?? 0, color: 'green'  },
        ]} />

        {/* Vehicle Trips */}
        <ModuleCard icon="🗺️" title="Vehicle Trips" link="/trips" accent={accent} pills={[
          { label: 'Planned',   value: trips.planned ?? 0,          color: 'yellow' },
          { label: 'On Trip',   value: trips.started ?? 0,          color: 'blue'   },
          { label: 'Done Today',value: trips.completed_today ?? 0,  color: 'green'  },
        ]} />

        {/* Drivers */}
        <ModuleCard icon="🧑‍✈️" title="Drivers" link="/drivers" accent={accent} pills={[
          { label: 'Available', value: drivers.available ?? 0, color: 'green'  },
          { label: 'Busy',      value: drivers.busy ?? 0,      color: 'yellow' },
          { label: 'On Trip',   value: drivers.on_trip ?? 0,   color: 'blue'   },
          { label: 'On Leave',  value: drivers.on_leave ?? 0,  color: 'gray'   },
        ]} />

        {/* Vehicles */}
        <ModuleCard icon="🚗" title="Vehicles" link="/vehicles" accent={accent} pills={[
          { label: 'Available',    value: vehicles.available ?? 0,   color: 'green'  },
          { label: 'Busy',         value: vehicles.busy ?? 0,        color: 'yellow' },
          { label: 'On Trip',      value: vehicles.on_trip ?? 0,     color: 'blue'   },
          { label: 'Maintenance',  value: vehicles.maintenance ?? 0, color: 'orange' },
        ]} />

        {/* Expenses */}
        <ModuleCard icon="💰" title="Expenses" link="/expenses" accent={accent} pills={[
          { label: 'Today',    value: 'PKR ' + pkr(expenses.today_total),   color: 'red'    },
          { label: '7 Days',   value: 'PKR ' + pkr(expenses.last_7d_total), color: 'orange' },
        ]} />

        {/* Expenses by Category */}
        <ModuleCard icon="📊" title="Expenses by Category" link="/expenses" accent={accent} pills={[
          { label: 'Visitor',     value: pkr(byCat.visitor),     color: 'blue'   },
          { label: 'Vehicle',     value: pkr(byCat.vehicle),     color: 'teal'   },
          { label: 'Employee',    value: pkr(byCat.employee),    color: 'indigo' },
          { label: 'Courier',     value: pkr(byCat.courier),     color: 'purple' },
          { label: 'Guest House', value: pkr(byCat.guest_house), color: 'orange' },
          { label: 'Other',       value: pkr(byCat.other),       color: 'gray'   },
        ]} />

        {/* Guest Rooms */}
        <ModuleCard icon="🏠" title="Guest Rooms" link="/rooms" accent={accent} pills={[
          { label: 'Scheduled', value: guestRooms.scheduled ?? 0,         color: 'yellow' },
          { label: 'In Use',    value: guestRooms.in_use ?? 0,            color: 'blue'   },
          { label: 'Out Today', value: guestRooms.checked_out_today ?? 0, color: 'green'  },
        ]} />

        {/* Hotel Bookings */}
        <ModuleCard icon="🏨" title="Hotel Bookings" link="/hotel-bookings" accent={accent} pills={[
          { label: 'Scheduled',   value: hotelBkgs.scheduled ?? 0,         color: 'yellow' },
          { label: 'Checked In',  value: hotelBkgs.checked_in ?? 0,        color: 'blue'   },
          { label: 'Out Today',   value: hotelBkgs.checked_out_today ?? 0, color: 'green'  },
        ]} />

        {/* Employee Vehicles */}
        <ModuleCard icon="🚗" title="Employee Vehicles" link="/employee-vehicles" accent={accent} pills={[
          { label: 'Total Records', value: empVehicleCount ?? 0, color: 'blue' },
        ]} />

      </div>

      {/* ── Today's Visitors Table ──────────────────────────────────────────── */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100">
        <div className="p-5 border-b border-gray-100 flex items-center justify-between">
          <h2 className="font-semibold text-gray-800 text-base">Today's Visitors</h2>
          <Link to="/visitors" className="text-sm font-medium hover:underline" style={{ color: accent }}>
            View All →
          </Link>
        </div>
        {recentVisitors.length === 0 ? (
          <div className="p-10 text-center text-gray-400">
            <div className="text-5xl mb-3">👥</div>
            <p className="text-sm">No visitors logged today</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50">
                <tr>
                  {['Name','Company','Purpose','Employee','In Time','Status'].map(h => (
                    <th key={h} className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {recentVisitors.map(v => (
                  <tr key={v.id} className="hover:bg-gray-50 transition-colors">
                    <td className="px-4 py-3 font-medium text-gray-800">{v.visitor_name}</td>
                    <td className="px-4 py-3 text-gray-600">{v.company_name || '—'}</td>
                    <td className="px-4 py-3">
                      <span className="px-2 py-1 bg-blue-50 text-blue-700 rounded-full text-xs">{v.purpose}</span>
                    </td>
                    <td className="px-4 py-3 text-gray-600">{v.employee_name || '—'}</td>
                    <td className="px-4 py-3 text-gray-600">
                      {v.in_time ? new Date(v.in_time).toLocaleTimeString('en-PK', { hour: '2-digit', minute: '2-digit' }) : '—'}
                    </td>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                        v.status === 'active'      ? 'bg-green-100 text-green-700' :
                        v.status === 'checked_out' ? 'bg-gray-100 text-gray-600'  :
                        'bg-yellow-100 text-yellow-700'
                      }`}>
                        {v.status === 'active' ? 'Inside' : v.status === 'checked_out' ? 'Checked Out' : 'Pending'}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

    </div>
  );
}
